import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { 
  Bell, Ban, Trash2, AlertTriangle, AlertCircle, CheckCircle2, 
  XCircle, Coins, FileText, BellRing, Check, ExternalLink, Clock,
  Sparkles, Filter, CheckCheck, MessageSquare, UserPlus, Heart,
  Star, Gift, Zap, Info
} from 'lucide-react';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}`);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, lu: true } : n
      ));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, lu: true })));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteNotification = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/notifications/${id}`);
      setTimeout(() => {
        setNotifications(notifications.filter(n => n.id !== id));
        setDeletingId(null);
      }, 300);
    } catch (err) {
      console.error(err);
      setDeletingId(null);
    }
  };

  // Déterminer le type et le style en fonction du contenu du message
  const getNotificationStyle = (message) => {
    if (!message) return { 
      icon: Bell, 
      iconBg: 'bg-gray-100', 
      iconColor: 'text-gray-500',
      accentColor: 'border-gray-300',
      glowColor: ''
    };
    
    const lowerMsg = message.toLowerCase();
    
    if (lowerMsg.includes('suspen')) {
      return { 
        icon: Ban, 
        iconBg: 'bg-red-100', 
        iconColor: 'text-red-600',
        accentColor: 'border-red-500',
        glowColor: 'shadow-red-100'
      };
    }
    if (lowerMsg.includes('supprim') || lowerMsg.includes('suppression')) {
      return { 
        icon: Trash2, 
        iconBg: 'bg-red-100', 
        iconColor: 'text-red-600',
        accentColor: 'border-red-500',
        glowColor: 'shadow-red-100'
      };
    }
    if (lowerMsg.includes('avertissement')) {
      return { 
        icon: AlertTriangle, 
        iconBg: 'bg-amber-100', 
        iconColor: 'text-amber-600',
        accentColor: 'border-amber-500',
        glowColor: 'shadow-amber-100'
      };
    }
    if (lowerMsg.includes('signale') || lowerMsg.includes('signalement')) {
      return { 
        icon: AlertCircle, 
        iconBg: 'bg-orange-100', 
        iconColor: 'text-orange-600',
        accentColor: 'border-orange-500',
        glowColor: 'shadow-orange-100'
      };
    }
    if (lowerMsg.includes('accepte') || lowerMsg.includes('acceptée') || lowerMsg.includes('approuv')) {
      return { 
        icon: CheckCircle2, 
        iconBg: 'bg-emerald-100', 
        iconColor: 'text-emerald-600',
        accentColor: 'border-emerald-500',
        glowColor: 'shadow-emerald-100'
      };
    }
    if (lowerMsg.includes('refuse') || lowerMsg.includes('refusée') || lowerMsg.includes('rejet')) {
      return { 
        icon: XCircle, 
        iconBg: 'bg-rose-100', 
        iconColor: 'text-rose-600',
        accentColor: 'border-rose-500',
        glowColor: 'shadow-rose-100'
      };
    }
    if (lowerMsg.includes('paiement') || lowerMsg.includes('payment') || lowerMsg.includes('argent') || lowerMsg.includes('xof')) {
      return { 
        icon: Coins, 
        iconBg: 'bg-emerald-100', 
        iconColor: 'text-emerald-600',
        accentColor: 'border-emerald-500',
        glowColor: 'shadow-emerald-100'
      };
    }
    if (lowerMsg.includes('candidature') || lowerMsg.includes('postul')) {
      return { 
        icon: FileText, 
        iconBg: 'bg-blue-100', 
        iconColor: 'text-blue-600',
        accentColor: 'border-blue-500',
        glowColor: 'shadow-blue-100'
      };
    }
    if (lowerMsg.includes('message') || lowerMsg.includes('commentaire')) {
      return { 
        icon: MessageSquare, 
        iconBg: 'bg-violet-100', 
        iconColor: 'text-violet-600',
        accentColor: 'border-violet-500',
        glowColor: 'shadow-violet-100'
      };
    }
    if (lowerMsg.includes('nouveau membre') || lowerMsg.includes('rejoint') || lowerMsg.includes('inscription')) {
      return { 
        icon: UserPlus, 
        iconBg: 'bg-cyan-100', 
        iconColor: 'text-cyan-600',
        accentColor: 'border-cyan-500',
        glowColor: 'shadow-cyan-100'
      };
    }
    if (lowerMsg.includes('like') || lowerMsg.includes('aimé') || lowerMsg.includes('favori')) {
      return { 
        icon: Heart, 
        iconBg: 'bg-pink-100', 
        iconColor: 'text-pink-600',
        accentColor: 'border-pink-500',
        glowColor: 'shadow-pink-100'
      };
    }
    if (lowerMsg.includes('note') || lowerMsg.includes('évaluation') || lowerMsg.includes('avis')) {
      return { 
        icon: Star, 
        iconBg: 'bg-yellow-100', 
        iconColor: 'text-yellow-600',
        accentColor: 'border-yellow-500',
        glowColor: 'shadow-yellow-100'
      };
    }
    if (lowerMsg.includes('bonus') || lowerMsg.includes('récompense') || lowerMsg.includes('cadeau')) {
      return { 
        icon: Gift, 
        iconBg: 'bg-purple-100', 
        iconColor: 'text-purple-600',
        accentColor: 'border-purple-500',
        glowColor: 'shadow-purple-100'
      };
    }
    if (lowerMsg.includes('urgent') || lowerMsg.includes('important') || lowerMsg.includes('rappel')) {
      return { 
        icon: Zap, 
        iconBg: 'bg-orange-100', 
        iconColor: 'text-orange-600',
        accentColor: 'border-orange-500',
        glowColor: 'shadow-orange-100'
      };
    }
    if (lowerMsg.includes('info') || lowerMsg.includes('mise à jour')) {
      return { 
        icon: Info, 
        iconBg: 'bg-sky-100', 
        iconColor: 'text-sky-600',
        accentColor: 'border-sky-500',
        glowColor: 'shadow-sky-100'
      };
    }
    
    return { 
      icon: Bell, 
      iconBg: 'bg-indigo-100', 
      iconColor: 'text-indigo-600',
      accentColor: 'border-indigo-500',
      glowColor: 'shadow-indigo-100'
    };
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.lu;
    if (filter === 'read') return n.lu;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.lu).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-indigo-200 rounded-full animate-spin border-t-indigo-600"></div>
            <BellRing className="w-6 h-6 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="mt-4 text-gray-500 font-medium">Chargement des notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Notifications
                </h1>
                <p className="text-sm text-gray-500">
                  {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Tout est à jour'}
                </p>
              </div>
            </div>
            
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all font-medium text-sm"
              >
                <CheckCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Tout marquer lu</span>
              </button>
            )}
          </div>
          
          {/* Filtres */}
          <div className="flex items-center gap-2 mt-4">
            <Filter className="w-4 h-4 text-gray-400" />
            {[
              { key: 'all', label: 'Toutes', count: notifications.length },
              { key: 'unread', label: 'Non lues', count: unreadCount },
              { key: 'read', label: 'Lues', count: notifications.length - unreadCount }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  filter === f.key
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {f.label}
                <span className={`ml-1.5 text-xs ${filter === f.key ? 'text-indigo-200' : 'text-gray-400'}`}>
                  ({f.count})
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-12 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-12 h-12 text-indigo-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              {filter === 'unread' ? 'Aucune notification non lue' : 
               filter === 'read' ? 'Aucune notification lue' : 'Aucune notification'}
            </h3>
            <p className="text-gray-500 max-w-sm mx-auto">
              {filter === 'unread' 
                ? 'Vous avez tout lu ! Revenez plus tard pour de nouvelles notifications.'
                : 'Les notifications apparaîtront ici quand vous en recevrez.'}
            </p>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium"
              >
                Voir toutes les notifications
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notif, index) => {
              const style = getNotificationStyle(notif.message);
              const IconComponent = style.icon;
              const isDeleting = deletingId === notif.id;
              
              return (
                <div
                  key={notif.id}
                  className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden ${
                    isDeleting ? 'opacity-0 scale-95 -translate-x-full' : 'opacity-100'
                  } ${!notif.lu ? `border-l-4 ${style.accentColor} ${style.glowColor}` : 'border border-gray-100'}`}
                  style={{ 
                    animationDelay: `${index * 50}ms`,
                    animation: 'fadeInUp 0.4s ease-out forwards'
                  }}
                >
                  {/* Indicateur non lu */}
                  {!notif.lu && (
                    <div className="absolute top-4 right-4">
                      <span className="flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
                      </span>
                    </div>
                  )}
                  
                  <div className="p-5">
                    <div className="flex gap-4">
                      {/* Icône */}
                      <div className={`flex-shrink-0 w-12 h-12 ${style.iconBg} rounded-2xl flex items-center justify-center`}>
                        <IconComponent className={`w-6 h-6 ${style.iconColor}`} />
                      </div>
                      
                      {/* Contenu */}
                      <div className="flex-1 min-w-0">
                        <div className={`text-gray-800 leading-relaxed ${!notif.lu ? 'font-medium' : ''}`}>
                          {notif.message}
                        </div>
                        
                        {/* Footer */}
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Clock className="w-4 h-4" />
                            <span>{getTimeAgo(notif.date_envoi)}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {notif.link && (
                              <button
                                onClick={() => {
                                  if (!notif.lu) markAsRead(notif.id);
                                  navigate(notif.link);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition shadow-md shadow-indigo-200"
                              >
                                <span>Voir</span>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {!notif.lu && (
                              <button
                                onClick={() => markAsRead(notif.id)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition"
                                title="Marquer comme lu"
                              >
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">Lu</span>
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notif.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Stats */}
        {notifications.length > 0 && (
          <div className="mt-8 text-center text-sm text-gray-400">
            {notifications.length} notification{notifications.length > 1 ? 's' : ''} au total
          </div>
        )}
      </div>
      
      {/* Style pour l'animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationsPage;
